import type {FC} from 'react'
import {useEffect} from 'react'
import {isRecoveryReloadInProgress} from '@shared/utils/recoveryReload'

interface UnsavedChangesDialogProps {
  hasUnsavedChanges: boolean
  onSave?: () => Promise<void> | void
  onDiscard?: () => void
}

/**
 * UnsavedChangesDialog - A component that warns users when they try to close
 * the browser tab/window while having unsaved changes.
 *
 * Note: In-app navigation blocking requires a data router (createBrowserRouter)
 * which is not currently used in this app. For in-app navigation warnings,
 * use manual confirmation dialogs before navigation actions.
 *
 * Usage:
 * ```tsx
 * <UnsavedChangesDialog
 *   hasUnsavedChanges={isDirty}
 * />
 * ```
 */
const UnsavedChangesDialog: FC<UnsavedChangesDialogProps> = ({hasUnsavedChanges}): null => {
  // Handle browser refresh/close - warns user before leaving the page
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Skip the prompt for the new-version banner's Refresh: the user
      // asked for it after being told to save, and the draft autosave
      // preserves the work. An automatic reload still prompts (AB#6757).
      if (isRecoveryReloadInProgress()) return
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  // This component doesn't render anything - it only adds the beforeunload listener
  return null
}

export default UnsavedChangesDialog
